import qs from 'qs'
import {
  checkHttpStatus,
  getApiBaseUrlWithOrg,
  getApiHeadersFromToken
} from 'utils/api'

export const fetchUserCompiledQuest = async (
  apiData,
  userCompiledQuestId,
  studentId
) => {
  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
    !apiData.isMentor ? 'user' : `users/${studentId}`
  }/user_compiled_quests/${userCompiledQuestId}`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchCompiledQuest = async (apiData, compiledQuestId) => {
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/compiled_quests/${compiledQuestId}`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    const cq = await response.json()
    return cq.compiled_quest
  } catch (err) {
    throw err
  }
}

export const fetchCourseOverview = async (apiData, courseSectionId) => {
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/course_sections/${courseSectionId}/course_overview`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchCourseSection = async (apiData, courseSectionId) => {
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/course_sections/${courseSectionId}`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchCourseSectionStudents = async (
  apiData,
  courseSectionId,
  levelId,
  questId
) => {
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/mentor/course_sections/${courseSectionId}/students`

  const progressUrl = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/mentor/course_sections/${courseSectionId}/levels/${levelId}/quests/${questId}/students_progress`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    const progressResponse = checkHttpStatus(
      await fetch(progressUrl, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    const students = await response.json()
    const progress = await progressResponse.json()

    return students.map(student => ({
      ...student,
      quest_progress: progress.find(p => p.user_id === student.id)
    }))
  } catch (err) {
    throw err
  }
}

export const fetchCourseSectionStudentsProgress = async (
  apiData,
  courseSectionId,
  levelId,
  questId
) => {
  const params = qs.stringify({
    q: {
      quest_id_eq: questId
    }
  })
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/mentor/course_sections/${courseSectionId}/levels/${levelId}/user_compiled_quests?${params}`
  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchTargetDates = async (
  apiData,
  courseSectionId,
  levelId,
  questId,
  studentId
) => {
  const targetDateParams = qs.stringify({
    q: {
      level_id_eq: levelId,
      quest_id_eq: questId
    }
  })

  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrlWithOrg(apiData.orgId)}/${
          !apiData.isMentor || !studentId ? 'user' : `users/${studentId}`
        }/course_sections/${courseSectionId}/quest_target_dates?${targetDateParams}`,
        {
          method: 'GET',
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchUserQuestActivityNotes = async (
  apiData,
  userCompiledQuestId,
  studentId
) => {
  const params = qs.stringify({
    q: {
      include_mentor_notifications: true
    }
  })

  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
    !apiData.isMentor ? 'user' : `users/${studentId}`
  }/compiled_quests/${userCompiledQuestId}/activity_notes?${
    apiData.isMentor ? params : ''
  }`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchUserQuestActivityWorkItems = async (
  apiData,
  userCompiledQuestId,
  studentId
) => {
  const params = qs.stringify({
    q: {
      include_mentor_notifications: true
    }
  })

  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
    !apiData.isMentor ? 'user' : `users/${studentId}`
  }/compiled_quests/${userCompiledQuestId}/work_items?${
    apiData.isMentor ? params : ''
  }`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    const workItems = await response.json()
    // filter out "shell" UWIs, which are created when file is uploaded
    // but window is closed or refreshed before user manually saves.
    return workItems.filter(a => {
      return !(a.artifact_type === 'attachment' && !a.url)
    })
  } catch (err) {
    throw err
  }
}

export const fetchUserQuestLogEvents = async (
  apiData,
  studentId,
  courseSectionId,
  levelId,
  questId
) => {
  const params = qs.stringify({
    q: {
      level_id_eq: levelId,
      quest_id_eq: questId
    }
  })

  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
    !apiData.isMentor ? 'user' : `users/${studentId}`
  }/course_sections/${courseSectionId}/quest_log_events?${params}`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchCompiledQuestSkills = async (
  apiData,
  compiledQuestId,
  gradeLevelId,
  frameworkSubjectId
) => {
  try {
    const params = qs.stringify({
      q: {
        grade_level_id_eq: gradeLevelId,
        framework_subject_id_eq: frameworkSubjectId,
        include_full_framework: true
      }
    })
    const url = `${getApiBaseUrlWithOrg(
      apiData.orgId
    )}/compiled_quests/${compiledQuestId}/framework_skills_framework?${params}`
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchCompiledQuestPeers = async (apiData, compiledQuestId) => {
  const url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/compiled_quests/${compiledQuestId}/active_users`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchUserCompiledQuestSkillChecks = async (
  apiData,
  userCompiledQuestId,
  studentId
) => {
  const params = qs.stringify({
    q: {
      include_mentor_notifications: true
    }
  })

  try {
    const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
      !apiData.isMentor ? 'user' : `users/${studentId}`
    }/framework_skills/user_compiled_quests/${userCompiledQuestId}/responses?${
      apiData.isMentor ? params : ''
    }`

    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchUserCompiledQuestPendingMentorFeedback = async (
  apiData,
  studentId,
  userCompiledQuestId
) => {
  try {
    const url = `${getApiBaseUrlWithOrg(
      apiData.orgId
    )}/users/${studentId}/compiled_quests/${userCompiledQuestId}/pending_mentor_feedback`

    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    return await response.json()
  } catch (err) {
    throw err
  }
}

export const fetchUserCompiledQuestArtifactAssessments = async (
  apiData,
  userCompiledQuestId,
  studentId = null
) => {
  try {
    const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
      !apiData.isMentor ? 'user' : `users/${studentId}`
    }/compiled_quests/${userCompiledQuestId}/artifact_assessments`

    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    return await response.json()
  } catch (err) {
    throw err
  }
}
